import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Step Checkbox Controller
// Handles auto-save on toggle, loading states, keyboard navigation, and ARIA announcements
export default class extends Controller {
  static targets = ["checkbox", "spinner", "announcement"]
  static values = { url: String }

  connect() {
    // Initialize ARIA attributes
    this.element.setAttribute("aria-busy", "false")
  }

  toggle(event) {
    event.preventDefault()
    this.submitToggle()
  }

  toggleWithKeyboard(event) {
    // Handle spacebar to toggle checkbox
    if (event.key === " ") {
      event.preventDefault()
      this.checkboxTarget.checked = !this.checkboxTarget.checked
      this.submitToggle()
    }
  }

  async submitToggle() {
    const isChecked = this.checkboxTarget.checked

    // Show loading state
    this.showLoading()

    // Announce to screen readers
    this.announce(isChecked ? "Marking step as complete" : "Marking step as incomplete")

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        // Process Turbo Stream response
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Announce completion
        this.announce(isChecked ? "Step completed" : "Step marked incomplete")
      } else {
        // Revert checkbox on error
        this.checkboxTarget.checked = !isChecked
        this.announce("Error updating step")
        console.error('Failed to toggle step')
      }
    } catch (error) {
      // Revert checkbox on error
      this.checkboxTarget.checked = !isChecked
      this.announce("Error updating step")
      console.error('Error toggling step:', error)
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    this.element.setAttribute("aria-busy", "true")
    this.checkboxTarget.disabled = true
    this.spinnerTarget.classList.remove("hidden")
  }

  hideLoading() {
    this.element.setAttribute("aria-busy", "false")
    this.checkboxTarget.disabled = false
    this.spinnerTarget.classList.add("hidden")
  }

  announce(message) {
    if (this.hasAnnouncementTarget) {
      this.announcementTarget.textContent = message
      // Clear after a delay
      setTimeout(() => {
        this.announcementTarget.textContent = ""
      }, 1000)
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
